import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import { ChartColor, getChartGridColor } from '@t-one-internal/design-system';

interface UnitPerformanceRow {
  scopeInstanceName: string;
  avgRating: number;
  employeeCount: number;
}

interface UnitPerformanceChartProps {
  data: Array<Record<string, unknown>>;
}

const COLORS = Object.values(ChartColor);

const toUnitPerformanceRow = (raw: Record<string, unknown>): UnitPerformanceRow => ({
  scopeInstanceName: (raw.scopeInstanceName as string) ?? 'Unknown',
  avgRating: Number(raw.avgRating ?? 0),
  employeeCount: Number(raw.employeeCount ?? 0),
});

const CustomTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: UnitPerformanceRow }>;
}) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '10px 14px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        border: '1px solid #E5E7EB',
      }}
    >
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
        {row.scopeInstanceName}
      </div>
      <div style={{ fontSize: '12px', color: '#6B7280', padding: '2px 0' }}>
        Avg Rating: <span style={{ color: '#111827', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{row.avgRating.toFixed(1)}</span>
      </div>
      <div style={{ fontSize: '12px', color: '#6B7280', padding: '2px 0' }}>
        Employees: <span style={{ color: '#111827', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{row.employeeCount}</span>
      </div>
    </div>
  );
};

export const UnitPerformanceChart: React.FC<UnitPerformanceChartProps> = ({ data }) => {
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    return data.map(toUnitPerformanceRow);
  }, [data]);

  if (chartData.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9CA3AF', fontSize: '13px' }}>
        No performance data available
      </div>
    );
  }

  const MIN_BAR_WIDTH = 90;
  const barCount = chartData.length;
  const needsScroll = barCount > 10;
  const scrollChartWidth = needsScroll ? barCount * MIN_BAR_WIDTH : undefined;
  const Y_AXIS_WIDTH = 36;

  const xAxisTick = (props: { x: number; y: number; payload: { value: string } }) => {
    const label = props.payload.value ?? '';
    const maxChars = 12;
    const words = label.split(' ');
    const lines: string[] = [];
    let current = '';
    for (const word of words) {
      if (current && (current + ' ' + word).length > maxChars) {
        lines.push(current);
        current = word;
      } else {
        current = current ? current + ' ' + word : word;
      }
    }
    if (current) lines.push(current);
    // Truncate to max 2 lines
    const truncatedLines = lines.slice(0, 2).map((line, i) => {
      if (i === 1 && lines.length > 2) {
        return line.length > 10 ? line.slice(0, 10) + '…' : line + '…';
      }
      return line;
    });
    return (
      <text x={props.x} y={props.y + 12} textAnchor="middle" fill="#6B7280" fontSize={11}>
        {truncatedLines.map((line, i) => (
          <tspan key={i} x={props.x} dy={i === 0 ? 0 : 14}>
            {line}
          </tspan>
        ))}
      </text>
    );
  };

  if (!needsScroll) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 40 }} maxBarSize={48}>
              <CartesianGrid stroke={getChartGridColor('axis')} strokeDasharray="none" vertical={false} />
              <XAxis
                dataKey="scopeInstanceName"
                type="category"
                tick={xAxisTick}
                axisLine={{ stroke: getChartGridColor('axis') }}
                tickLine={false}
                interval={0}
              />
              <YAxis
                domain={[0, 5]}
                ticks={[0, 1, 2, 3, 4, 5]}
                tick={{ fontSize: 12, fill: '#9CA3AF' }}
                axisLine={{ stroke: getChartGridColor('axis') }}
                tickLine={false}
                width={Y_AXIS_WIDTH}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
              <Bar dataKey="avgRating" radius={[4, 4, 0, 0]}>
                {chartData.map((_, idx) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }

  // Scrollable layout: fixed Y-axis on left + scrollable bar area on right
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {/* Fixed Y-axis */}
        <div style={{ width: Y_AXIS_WIDTH + 8, flexShrink: 0, height: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={[chartData[0]]} margin={{ top: 4, right: 0, left: 0, bottom: 40 }}>
              <CartesianGrid stroke="transparent" vertical={false} horizontal={false} />
              <XAxis dataKey="scopeInstanceName" tick={() => <text />} axisLine={false} tickLine={false} />
              <YAxis
                domain={[0, 5]}
                ticks={[0, 1, 2, 3, 4, 5]}
                tick={{ fontSize: 12, fill: '#9CA3AF' }}
                axisLine={{ stroke: getChartGridColor('axis') }}
                tickLine={false}
                width={Y_AXIS_WIDTH}
              />
              <Bar dataKey="avgRating" fill="transparent" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Scrollable bar chart */}
        <div className="no-scrollbar" style={{ flex: 1, minWidth: 0, overflowX: 'auto', overflowY: 'hidden', height: '100%', scrollbarWidth: 'none' }}>
          <div style={{ minWidth: scrollChartWidth, height: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 40 }} maxBarSize={48}>
                <CartesianGrid stroke={getChartGridColor('axis')} strokeDasharray="none" vertical={false} />
                <XAxis
                  dataKey="scopeInstanceName"
                  type="category"
                  tick={xAxisTick}
                  axisLine={{ stroke: getChartGridColor('axis') }}
                  tickLine={false}
                  interval={0}
                />
                <YAxis hide />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="avgRating" radius={[4, 4, 0, 0]}>
                  {chartData.map((_, idx) => (
                    <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};
